import DragIndicator from "@mui/icons-material/DragIndicator";
import { Box } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// LeftPanelResizer — vertical drag handle that lets the user resize the
// rule-builder's left column (toolbox or active form panel) between sensible
// limits. Persists the chosen width to localStorage so refreshes keep it.
//
// Usage: render once between the left column and the canvas. The panel reads
// `useLeftPanelWidth()` to pick its width.
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = "coapp-rule-builder-left-panel-width";
// 520 is the smallest width where the longest two-button form footer
// ("Cancel" + "Save Formula Variable") + their padding fits on one row
// without the primary button wrapping its label.
export const LEFT_PANEL_MIN = 520;
export const LEFT_PANEL_MAX = 820;
export const LEFT_PANEL_DEFAULT = 540;

const readStored = (): number => {
	if (typeof window === "undefined") return LEFT_PANEL_DEFAULT;
	const raw = window.localStorage?.getItem(STORAGE_KEY);
	if (!raw) return LEFT_PANEL_DEFAULT;
	const n = Number.parseInt(raw, 10);
	if (Number.isNaN(n)) return LEFT_PANEL_DEFAULT;
	return Math.max(LEFT_PANEL_MIN, Math.min(LEFT_PANEL_MAX, n));
};

/** Read the persisted width once on mount. */
export function useLeftPanelWidth() {
	const [width, setWidth] = useState<number>(readStored);

	const persist = useCallback((next: number) => {
		const clamped = Math.max(LEFT_PANEL_MIN, Math.min(LEFT_PANEL_MAX, next));
		setWidth(clamped);
		try {
			window.localStorage?.setItem(STORAGE_KEY, String(clamped));
		} catch {
			// ignore storage errors (private mode, quota)
		}
	}, []);

	return { width, setWidth: persist };
}

interface LeftPanelResizerProps {
	width: number;
	onWidthChange: (next: number) => void;
}

/**
 * Vertical drag handle between the left form/toolbox pane and the canvas.
 *
 * Layout: a 4px-wide hairline rail in normal flex flow, with a circular
 * "grip" chip absolutely positioned at the rail's vertical center that
 * OVERHANGS the canvas (translateX +50%). The chip is the visual
 * affordance — clicking and dragging anywhere along the rail OR the chip
 * resizes the pane.
 */
export default function LeftPanelResizer({ width, onWidthChange }: LeftPanelResizerProps) {
	const draggingRef = useRef<{ startX: number; startWidth: number } | null>(null);
	const [hovered, setHovered] = useState(false);
	const [dragging, setDragging] = useState(false);

	const onPointerDown = useCallback(
		(e: React.PointerEvent) => {
			e.preventDefault();
			draggingRef.current = { startX: e.clientX, startWidth: width };
			setDragging(true);
			(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
		},
		[width]
	);

	const onPointerMove = useCallback(
		(e: React.PointerEvent) => {
			const start = draggingRef.current;
			if (!start) return;
			const next = start.startWidth + (e.clientX - start.startX);
			onWidthChange(next);
		},
		[onWidthChange]
	);

	const onPointerUp = useCallback((e: React.PointerEvent) => {
		draggingRef.current = null;
		setDragging(false);
		try {
			(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
		} catch {
			// no-op
		}
	}, []);

	// Cleanup if user navigates away mid-drag.
	useEffect(() => () => { draggingRef.current = null; }, []);

	const active = hovered || dragging;

	// Keyboard resize: Arrow Left/Right ±16px, Shift+Arrow ±64px, Home/End
	// snap to min/max. Satisfies WCAG 2.1 AA 2.1.1 — drag must have a
	// keyboard alternative.
	const onKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			let delta = 0;
			const big = e.shiftKey ? 64 : 16;
			if (e.key === "ArrowLeft")  delta = -big;
			else if (e.key === "ArrowRight") delta = big;
			else if (e.key === "Home")  { onWidthChange(LEFT_PANEL_MIN); e.preventDefault(); return; }
			else if (e.key === "End")   { onWidthChange(LEFT_PANEL_MAX); e.preventDefault(); return; }
			if (delta) {
				e.preventDefault();
				onWidthChange(width + delta);
			}
		},
		[width, onWidthChange]
	);

	return (
		<Box
			role="separator"
			aria-orientation="vertical"
			aria-label="Resize left panel — use arrow keys to adjust"
			aria-valuenow={width}
			aria-valuemin={LEFT_PANEL_MIN}
			aria-valuemax={LEFT_PANEL_MAX}
			tabIndex={0}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerUp}
			onPointerCancel={onPointerUp}
			onKeyDown={onKeyDown}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			sx={{
				position: "relative",
				flex: "0 0 4px",
				width: 4,
				height: "100%",
				cursor: "col-resize",
				background: active ? "rgba(2, 213, 171, 0.55)" : "rgba(13, 31, 48, 0.06)",
				transition: "background 0.18s ease",
				zIndex: 5,
				// Grip chip — overhangs the canvas edge so it's findable.
				"&::after": {
					content: "\"\"",
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					width: 22,
					height: 38,
					borderRadius: 999,
					background: active
						? "linear-gradient(135deg, #02D5AB 0%, #0A7FA8 100%)"
						: "#FFFFFF",
					border: active ? "1.5px solid #FFFFFF" : "1.5px solid rgba(13, 31, 48, 0.10)",
					boxShadow: active
						? "0 4px 12px rgba(2, 213, 171, 0.30)"
						: "0 2px 6px rgba(13, 31, 48, 0.10)",
					transition: "background 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
					pointerEvents: "none",
				},
			}}
		>
			{/* SVG grip dots, centered on top of the chip. zIndex lifts the icon
			    ABOVE the `&::after` pill: pseudo-elements paint after in-flow
			    children, so without this the gradient pill covered the drag grip
			    and the pill looked blank (no drag affordance). */}
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					zIndex: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					width: 22,
					height: 38,
					pointerEvents: "none",
					color: active ? "#FFFFFF" : "rgba(13, 31, 48, 0.45)",
					transition: "color 0.18s ease",
				}}
			>
				<DragIndicator sx={{ fontSize: 18 }} />
			</Box>
		</Box>
	);
}
